import { css } from "@emotion/react";
import { Fragment, Node as TiptapNode } from "@tiptap/pm/model";
import { Decoration } from "@tiptap/pm/view";

import { ANNOTABLE_INLINE_NODE_TYPES } from "../Annotations";
import { IAnnotationDecoration } from "../TextHighlighting.interface";
import {
  annotationColors,
  annotationColorsDarker,
  annotationColorsLighter,
} from "../TextHighlighting.style";

export const setUpDecorations = (
  doc: TiptapNode,
  annotation: IAnnotationDecoration,
): Decoration[] => {
  const { startIndex, endIndex, style = { penName: "yellow" } } = annotation;
  const { background, borderColor, penName = "yellow" } = style;

  const fragment = doc.slice(
    startIndex,
    Math.min(endIndex, doc.content.size),
  ).content;

  const fragmentAttrs = {
    style: css({
      background: background || annotationColorsLighter[penName],
      borderBottom: `2px solid ${borderColor || annotationColorsDarker[penName]}`,
      cursor: "pointer",
      "&:hover": {
        background: background || annotationColors[penName],
      },
    }).styles,
  };

  let offset = 0;

  const decorations: Decoration[] = [];
  try {
    const nodes = flattenNodeList(fragment);
    for (const node of nodes) {
      if (typeof node.nodeSize !== "number") {
        console.warn("Skipping node with undefined nodeSize:", node);
        continue;
      }
      const { nodeSize, type } = node;
      const from = startIndex + offset;
      const to = from + nodeSize;
      offset += nodeSize;

      const isAnnotableNode = ANNOTABLE_INLINE_NODE_TYPES.includes(type.name);
      decorations.push(
        isAnnotableNode
          ? Decoration.node(from, to, fragmentAttrs)
          : Decoration.inline(from, to, fragmentAttrs),
      );
    }
  } catch (error) {
    console.error(error);
  }

  return decorations;
};

/*
TODO:
  Implement a child-first traversal of the nodes,
  potentially using descendants or a similar approach,
  to optimize memory usage.
*/
export const flattenNodeList = (
  fragment: Fragment | TiptapNode,
): TiptapNode[] => {
  let nodes: TiptapNode[] = [];
  fragment.forEach((child) => {
    const isText =
      child.isText || ANNOTABLE_INLINE_NODE_TYPES.includes(child.type.name);

    nodes = isText ? [...nodes, child] : [...nodes, ...flattenNodeList(child)];
  });

  return nodes;
};

// Helper function to determine if text was clicked
export const isTextClicked = (target: HTMLElement): boolean => {
  // Check if the node itself is a text node
  if (target.nodeType === Node.TEXT_NODE) {
    return true;
  }

  // Check if the node is an element node that's not a <div>
  if (target.nodeType === Node.ELEMENT_NODE) {
    const element = target as HTMLElement;
    // Return true only for elements that are not <div> and that
    // the element contains a non-empty text content
    return (
      element.tagName.toLowerCase() !== "div" &&
      element.textContent?.trim() !== ""
    );
  }

  // Return false for any other types of nodes
  return false;
};
